import { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormattingContext } from '../../organisms/FormattingProvider/FormattingContext'
import { useChartContext } from './ChartProvider'

/**
 * Simple template formatter for ECharts tooltip callback
 * Replaces placeholders like {name}, {marker}, {value[0]}, etc.
 * Supports currying for use as a formatter callback.
 *
 * @param {string} template - template string with {tokens}
 * @param {object|function} [valueFormatMap] - map of formatters per token, format strings, or a global formatter function
 * @param {function} [internalFormatter] - optional shared formatting function (val, formatStr)
 * @returns {function|any} - a formatter callback if template is string, otherwise returns template as-is
 */
export function formatTemplate (template, valueFormatMap, internalFormatter) {
  if (typeof template !== 'string') return template

  return function (params) {
    return template.replace(/\{([^}]+)\}/g, (_, key) => {
      const path = key.replace(/\[(\w+)\]/g, '.$1').split('.')
      let val = params
      for (const prop of path) {
        if (val == null) return ''
        val = val[prop]
      }

      if (typeof valueFormatMap === 'function') {
        return valueFormatMap(val, key)
      } else if (valueFormatMap && typeof valueFormatMap[key] === 'function') {
        return valueFormatMap[key](val)
      } else if (internalFormatter && typeof valueFormatMap?.[key] === 'string') {
        return internalFormatter(val, valueFormatMap[key])
      }

      return val != null ? String(val) : ''
    })
  }
}

function Tooltip ({
  show,
  trigger,
  axisPointer,
  showContent,
  alwaysShowContent,
  triggerOn,
  showDelay,
  hideDelay,
  enterable,
  renderMode,
  confine,
  appendTo,
  className,
  transitionDuration,
  position,
  formatter,
  valueFormatter,
  valueFormat,
  backgroundColor,
  borderColor,
  borderWidth,
  padding,
  textStyle,
  extraCssText,
  order,
  valueFormatMap
}) {
  const { setKeyConfig } = useChartContext()
  const { formatter: _internalFormatter } = useFormattingContext()
  const _formatInternal = useCallback((d, i) => {
    if (valueFormatter) return valueFormatter(d, i)
    const format = Array.isArray(valueFormat) ? valueFormat[0] : valueFormat
    return _internalFormatter(d, format)
  }, [valueFormat, _internalFormatter, valueFormatter])

  const computedValueFormatMap = useMemo(() => {
    if (!Array.isArray(valueFormat) || typeof formatter !== 'string') {
      return valueFormatMap
    }

    const arrayFormatMap = {}
    valueFormat.forEach((format, index) => {
      arrayFormatMap[`value[${index}]`] = (val) => _internalFormatter(val, format)
    })

    if (typeof valueFormatMap === 'function') {
      return (val, key) => {
        if (arrayFormatMap[key]) return arrayFormatMap[key](val)
        return valueFormatMap(val, key)
      }
    }

    return { ...arrayFormatMap, ...(valueFormatMap || {}) }
  }, [valueFormat, valueFormatMap, _internalFormatter, formatter])

  useEffect(() => {
    const config = {
      show,
      trigger,
      axisPointer,
      showContent,
      alwaysShowContent,
      triggerOn,
      showDelay,
      hideDelay,
      enterable,
      renderMode,
      confine,
      appendTo,
      className,
      transitionDuration,
      position,
      formatter: formatTemplate(formatter, computedValueFormatMap, _internalFormatter),
      valueFormatter: _formatInternal,
      backgroundColor,
      borderColor,
      borderWidth,
      padding,
      textStyle,
      extraCssText,
      order
    }

    Object.keys(config).forEach((key) => {
      if (typeof config[key] === 'undefined') {
        delete config[key]
      }
    })

    setKeyConfig('tooltip', config)
  }, [
    show,
    trigger,
    axisPointer,
    showContent,
    alwaysShowContent,
    triggerOn,
    showDelay,
    hideDelay,
    enterable,
    renderMode,
    confine,
    appendTo,
    className,
    transitionDuration,
    position,
    formatter,
    valueFormatter,
    backgroundColor,
    borderColor,
    borderWidth,
    padding,
    textStyle,
    extraCssText,
    order,
    _formatInternal,
    _internalFormatter,
    setKeyConfig,
    computedValueFormatMap
  ])

  return null
}

Tooltip.propTypes = {
  /** Whether to show the tooltip component. including tooltip floating layer and axisPointer. */
  show: PropTypes.bool,

  /** Type of triggering.
   Options:
   'item' - Triggered by data item, which is mainly used for charts that don't have a category axis like scatter charts or pie charts.
   'axis' - Triggered by axes, which is mainly used for charts that have category axes, like bar charts or line charts.
            ECharts 2.x only supports axis trigger for category axis. In ECharts 3, it is supported for all types of axes in grid or polar. Also, you may assign axis with axisPointer.axis.
   'none' - Trigger nothing. */
  trigger: PropTypes.oneOf(['item', 'axis', 'none']),

  /** Configuration item for axisPointer.
   tooltip.axisPointer is like syntactic sugar of axisPointer settings on axes (for example, xAxis.axisPointer or angleAxis.axisPointer).
   More detailed features can be configured on someAxis.axisPointer. But in common cases, using tooltip.axisPointer is more convenient. */
  axisPointer: PropTypes.object,

  /** Whether to show the tooltip floating layer, whose default value is true. It should be configurated to be false, if you only need tooltip to trigger the event or show the axisPointer without content. */
  showContent: PropTypes.bool,

  /** Whether to show tooltip content all the time. By default, it will be hidden after some time. It can be set to be true to preserve displaying.
   This attribute is newly added to ECharts 3.0. */
  alwaysShowContent: PropTypes.bool,

  /** Conditions to trigger tooltip. Options:
   'mousemove' - Trigger when mouse moves.
   'click' - Trigger when mouse clicks.
   'mousemove|click' - Trigger when mouse clicks and moves.
   'none' - Do not triggered by 'mousemove' and 'click'. Tooltip can be triggered and hidden manually by calling action.tooltip.showTip and action.tooltip.hideTip. It can also be triggered by axisPointer.handle in this case.
   */
  triggerOn: PropTypes.oneOf(['mousemove', 'click', 'mousemove|click', 'none']),

  /** Delay time for showing tooltip, in ms. No delay by default, and it is not recommended to set. Only valid when triggerOn is set to be 'mousemove'. */
  showDelay: PropTypes.number,

  /** Delay time for hiding tooltip, in ms. It will be invalid when alwaysShowContent is true. */
  hideDelay: PropTypes.number,

  /** Whether mouse is allowed to enter the floating layer of tooltip, whose default value is false. If you need to interact in the tooltip like with links or buttons, it can be set as true. */
  enterable: PropTypes.bool,

  /** Render mode for tooltip. By default, it is set to be 'html' so that extra DOM element is used for tooltip. It can also set to be 'richText' so that the tooltip will be rendered inside Canvas. This is very useful for environments that don't have DOM, such as Wechat applications. */
  renderMode: PropTypes.oneOf(['html', 'richText']),

  /** Whether confine tooltip content in the view rect of chart instance.
   Useful when tooltip is cut because of 'overflow: hidden' set on outer dom of chart instance, or because of narrow screen on mobile. */
  confine: PropTypes.bool,

  /** Which DOM element to append the tooltip to. Only available when using renderMode 'html'. */
  appendTo: PropTypes.string,

  /** Specify the classes for the tooltip root DOM. (Only works in html render mode). */
  className: PropTypes.string,

  /** The transition duration of tooltip's animation, in seconds. When it is set to be 0, it would move closely with the mouse. */
  transitionDuration: PropTypes.number,

  /** The position of the tooltip's floating layer, which would follow the position of mouse by default. */
  position: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),

  /** The tooltip formatting string */
  formatter: PropTypes.string,

  /** Callback function for formatting the value section in tooltip. */
  valueFormatter: PropTypes.func,

  /** summit string format (single or array for multi-value tooltips) */
  valueFormat: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),

  /** The background color of tooltip's floating layer. */
  backgroundColor: PropTypes.string,

  /** The border color of tooltip's floating layer. */
  borderColor: PropTypes.string,

  /** The border width of tooltip's floating layer. */
  borderWidth: PropTypes.number,

  /** The floating layer of tooltip space around content. The unit is px. Default values for each position are 5. And they can be set to different values with left, right, top, and bottom. */
  padding: PropTypes.number,

  /** The text style of tooltip's floating layer. */
  textStyle: PropTypes.object,

  /** Extra CSS style for floating layer. The following is an example for adding shadow. */
  extraCssText: PropTypes.string,

  /** Tooltip order for multiple series. Defaults is 'seriesAsc'.
   Conditions to order tooltip. Options:
   'seriesAsc' - Base on series declaration, ascending order tooltip.
   'seriesDesc' - Base on series declaration, descending order tooltip.
   'valueAsc' - Base on value, ascending order tooltip, only for numeric value.
   'valueDesc' - Base on value, descending order tooltip, only for numeric value. */
  order: PropTypes.oneOf(['seriesAsc', 'seriesDesc', 'valueAsc', 'valueDesc']),

  valueFormatMap: PropTypes.object
}

Tooltip.defaultProps = {
  show: true,
  renderMode: 'html'
}

export default Tooltip
